import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import { useDispatch, useSelector } from "react-redux";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import { RootState } from "../../../store";
import { toast } from "react-toastify";
const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface GPData {
    gp_lgd_code: number,
    gp_name: string,
    total_meetings_uploaded: number,
    draft_mom: number,
    final_submitted_mom: number
}

const GramDashboard4: React.FC<{ finYear: string; meetingType: any }> = ({
    finYear,
    meetingType,
}) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const dispatch = useDispatch();
    const [tableData, setTableData] = useState<GPData[]>([]);
    const [filterText, setFilterText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const gpData = useSelector(
        (state: RootState) => state.common.dashboardData["Gram Panchayats"]
    );
    const isBpDirectState = Number(gpData?.state_code) === 34;
    const parentCode = isBpDirectState ? gpData?.bp_lgd_code : gpData?.zp_lgd_code;

    useEffect(() => {
        const fetchGPs = async () => {
            setTableData([]);
            if (!gpData?.state_code || !gpData?.bp_lgd_code || !parentCode) return;


            try {
                const response = await axios.get(
                    `${baseUrl}api/gp_report/${gpData.state_code}/${parentCode}/${gpData.bp_lgd_code}`,
                    {
                        params: {
                            fin_year: finYear,
                            meeting_type:
                                meetingType.value !== "0" ? meetingType.value : undefined,
                        },
                    }
                );
                if (response?.data?.msg_code === 400) {
                    toast.error(response?.data?.msg || "Something went wrong");
                    setTableData([]);
                    return;
                }
                const mappedData: GPData[] =
                    response?.data?.data?.map((item: any) => ({
                        gp_lgd_code: item.gp_lgd_code,
                        gp_name: item.gp_name,
                        total_meetings_uploaded: item.total_meetings_uploaded,
                        draft_mom: item.draft_mom || 0,
                        final_submitted_mom: item.final_submitted_mom || 0
                    })) || [];

                setTableData(mappedData);
            } catch (error) {
                console.error("Error fetching GP data:", error);
            }
        };

        fetchGPs();
    }, [
        finYear,
        meetingType,
        baseUrl,
        gpData?.state_code,
        gpData?.zp_lgd_code,
        gpData?.bp_lgd_code,
        parentCode,
    ]);
    const handleBack = () => {
        dispatch(
            updateDashboardFields({
                menu: "Gram Panchayats",
                fields: {
                    ...(isBpDirectState
                        ? {
                            zp_lgd_code: null,
                            zp_name: "",
                        }
                        : {}),
                    bp_lgd_code: null,
                    bp_name: "",
                },
            })
        );
    };
    const handleMomHighlight = (zpCode: number, zpName: string) => {
        dispatch(
            updateDashboardFields({
                menu: "Gram Panchayats",
                fields: {
                    gp_lgd_code: zpCode,
                    gp_name: zpName,
                },
            })
        );
    };

    const columns: TableColumn<GPData>[] = [
        {
            name: "S.No.",
            cell: (_row, index) =>
                (index + 1 + (currentPage - 1) * rowsPerPage).toString(),
            width: "80px",
        },
        {
            name: "GP Name",
            selector: (row) => row.gp_name,
            cell: (row) => (
                <span
                    className={row.total_meetings_uploaded > 0 ? "highlight-td" : ""}
                    style={{
                        cursor: row.total_meetings_uploaded > 0 ? "pointer" : "default",
                    }}
                    onClick={() =>
                        row.total_meetings_uploaded > 0 &&
                        handleMomHighlight(row.gp_lgd_code, row.gp_name)
                    }
                >
                    {row.gp_name}
                </span>
            ),
            sortable: true,
        },
        { name: "Draft MoM", selector: (row) => row.draft_mom, sortable: true },
        { name: "Final Submitted", selector: (row) => row.final_submitted_mom, sortable: true },
        {
            name: "Total Meetings Uploaded",
            selector: (row) => row.total_meetings_uploaded,
            sortable: true,
        },
    ];

    const filteredData = tableData.filter(
        (item) =>
            item.gp_name?.toLowerCase().includes(filterText?.toLowerCase()) ||
            String(item.gp_lgd_code).includes(filterText)
    );


    const exportExcel = () => {
        const header = [
            "S.No.",
            "GP Name",
            "Draft MoM",
            "Final Submitted",
            "Total Meetings Uploaded",
        ];

        const data = filteredData.map((row, index) => [
            index + 1,
            row.gp_name,
            row.draft_mom,
            row.final_submitted_mom,
            row.total_meetings_uploaded,
        ]);

        const totalDraft = filteredData.reduce((sum, r) => sum + r.draft_mom, 0);
        const totalFinal = filteredData.reduce((sum, r) => sum + r.final_submitted_mom, 0);
        const totalMeetingsUploaded = filteredData.reduce(
            (sum, r) => sum + r.total_meetings_uploaded,
            0
        );

        const totalRow = [
            "",
            "Total",
            totalDraft,
            totalFinal,
            totalMeetingsUploaded,
        ];

        const finalData = [header, ...data, totalRow];

        const ws = XLSX.utils.aoa_to_sheet(finalData);

        header.forEach((_, colIdx) => {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIdx });
            if (!ws[cellAddress]) return;

            ws[cellAddress].s = {
                font: { bold: true, sz: 13 },
                alignment: { horizontal: "center", vertical: "center" },
            };
        });

        const totalRowIndex = finalData.length - 1;

        header.forEach((_, colIdx) => {
            const cellAddress = XLSX.utils.encode_cell({
                r: totalRowIndex,
                c: colIdx,
            });

            if (!ws[cellAddress]) return;

            ws[cellAddress].s = {
                font: { bold: true, sz: 13 },
                alignment: {
                    horizontal: colIdx >= 2 ? "right" : "left",
                    vertical: "center",
                },
            };
        });

        ws["!cols"] = [
            { wch: 8 },
            { wch: 35 },
            { wch: 15 },
            { wch: 15 },
            { wch: 25 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "GPReport");
        XLSX.writeFile(wb, "GPReport.xlsx");
    };


    const totalDraft = filteredData.reduce((sum, r) => sum + r.draft_mom, 0);
    const totalFinal = filteredData.reduce((sum, r) => sum + r.final_submitted_mom, 0);
    const totalMeetingsUploaded = filteredData.reduce((sum, r) => sum + r.total_meetings_uploaded, 0);

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex">
                    <button className="back-btn me-2" onClick={handleBack}>
                        &larr; Back
                    </button>
                    <div className="data-chip me-2">
                        {gpData?.state_name} - {gpData?.state_code}
                    </div>
                    {!isBpDirectState && (
                        <div className="data-chip me-2">
                            {gpData?.zp_name} - {gpData?.zp_lgd_code}
                        </div>
                    )}
                    <div className="data-chip me-2">
                        {gpData?.bp_name} - {gpData?.bp_lgd_code}
                    </div>

                </div>
                <div>
                    <button className="shiny-btn me-2" onClick={exportExcel}>
                        <FaFileExcel /> Export Excel
                    </button>
                </div>
            </div>

            <input
                type="text"
                className="form-control d-inline-block w-25"
                placeholder="Search by GP name or code..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
            />

            <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationPerPage={rowsPerPage}
                onChangePage={(page) => setCurrentPage(page)}
                onChangeRowsPerPage={(newPerPage, page) => {
                    setRowsPerPage(newPerPage);
                    setCurrentPage(page);
                }}
                highlightOnHover
                striped
                noDataComponent="No data available"
            />

            {
                filteredData.length > 0 && <div className="table-footer fw-bold bg-light">
                    <div style={{ width: "80px" }}></div>
                    <div className="flex-fill">Total</div>
                    <div className="flex-fill">{totalDraft}</div>
                    <div className="flex-fill">{totalFinal}</div>
                    <div className="flex-fill">{totalMeetingsUploaded}</div>
                </div>
            }
        </div>
    )
}

export default GramDashboard4;